import React, { useLayoutEffect, useRef, useState } from 'react'
import { Color, Direction, Layout } from '../../../../utils/constants'
import Category, { CategoryType } from '../../../../components/Category'
import { ReactComponent as Account } from '../../../../assets/icons/account.svg'
import Button from '../../../../components/Button/Button'
import Tag from '../../../../components/Tag'
import PageHeader from '../../../../partials/PageHeader'
import Separator from '../../../../components/Separator'
import parse from 'html-react-parser'
import SectionWithTag, { Side } from '../../../../components/SectionWithTag'
import { WP_REST_API_Post } from 'wp-types/index'
import SimilarResources from '../SimilarResources'
import FadeIn from '../../../../components/Animation/FadeIn'
import { useIsMobile } from '../../../../utils/helpers'

interface PublicationProps {
  resource: WP_REST_API_Post | null
  tags: string[]
}

const Publication = ({ resource, tags }: PublicationProps) => {
    const headerRef = useRef<HTMLDivElement>(null)
    const [padding, setPadding] = useState({ paddingLeft: 0, paddingRight: 0 })
    const isMobile = useIsMobile()

    useLayoutEffect(() => {
        const calculatePadding = () => {
            requestAnimationFrame(() => {
                if (headerRef.current && !isMobile) {
                    setPadding({
                        paddingLeft: headerRef.current.getBoundingClientRect().left,
                        paddingRight: window.innerWidth - headerRef.current.getBoundingClientRect().right,
                    })
                } else {
                    setPadding({ paddingLeft: 0, paddingRight: 0 })
                }
            })
        }

        calculatePadding()
        window.addEventListener('resize', calculatePadding)
        return () => {
            window.removeEventListener('resize', calculatePadding)
        }
    }, [headerRef, isMobile])
    return (
        <div>
            <PageHeader
                bgColor={Color.WHITE}
                link={`/resources/${CategoryType.PUBLICATION}`}
                linkTitle={['page.resources', `page.resources.${CategoryType.PUBLICATION}`]}
            >
                <div />
            </PageHeader>
            <div className='py-12 side-padding md:side-padding-md mx-auto 2xl:max-w-screen-2xl xl:max-w-screen-xl'>
                <div className='flex justify-between md:flex-row flex-col gap-x-6' ref={headerRef}>
                    <FadeIn appearFromBottom>
                        <div className='max-w-[744px]'>
                            <Category
                                type={CategoryType.PUBLICATION}
                                className='bg-transparent backdrop-blur-none px-0'
                            />
                            <h3 className='py-8'>{resource?.title?.rendered}</h3>
                            <div className='md:hidden pb-5'>
                                {/*@ts-ignore*/}
                                <a href={resource?.acf.link} target='_blank' rel='noopener noreferrer'>
                                    <Button
                                        buttonColor={Color.PURPLE}
                                        isInverted
                                        chevron={Direction.RIGHT}
                                        outerClasses='w-full'
                                    >
                                        <h6>Read full article</h6>
                                    </Button>
                                </a>
                                {tags.length > 0 && (
                                    <div className='max-w-[240px]'>
                                        <h6 className='pb-2 pt-5'>Tags:</h6>
                                        <div className='flex flex-wrap gap-2'>
                                            {tags.map((tag) => (
                                                <Tag title={tag} color={Color.BLACK} key={tag} />
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className='border-y border-black/10 py-3.5 flex justify-between md:items-center md:flex-row flex-col gap-y-4'>
                                <div className='flex items-start gap-x-3'>
                                    <Account className='w-4 stroke-purple flex-shrink-0' />
                                    {/*@ts-ignore*/}
                                    <p className='text-xs text-purple max-w-[385px]'>{resource?.acf.authors}</p>
                                </div>
                                <div className='flex'>
                                    {/*@ts-ignore*/}
                                    <p className='text-xs text-purple'>{resource?.acf.journal}</p>
                                    <Separator direction={Layout.VERTICAL} />
                                    {/*@ts-ignore*/}
                                    <p className='text-xs text-purple'>{resource?.acf.publication_date}</p>
                                </div>
                            </div>
                        </div>
                    </FadeIn>
                    <FadeIn>
                        <div className='hidden md:block'>
                            {/*@ts-ignore*/}
                            <a href={resource?.acf.link} target='_blank' rel='noopener noreferrer'>
                                <Button
                                    buttonColor={Color.PURPLE}
                                    isInverted
                                    chevron={Direction.RIGHT}
                                    className='ga-cta'
                                    id={resource?.slug}
                                >
                                    <h6>Read full article</h6>
                                </Button>
                            </a>
                            <div className='max-w-[240px]'>
                                <h6 className='pb-2 pt-9'>Tags:</h6>
                                <div className='flex flex-wrap gap-2'>
                                    {tags.map((tag) => (
                                        <Tag title={tag} color={Color.BLACK} key={tag} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </FadeIn>
                </div>
            </div>
            <SectionWithTag
                tag={{ id: 'category.publication', className: 'md:pt-12' }}
                side={Side.LEFT}
                padding={padding}
            >
                <FadeIn>
                    <div className='max-w-[744px] pb-30 pt-12 resource-body'>
                        {resource && parse(resource?.content.rendered)}
                    </div>
                </FadeIn>
            </SectionWithTag>
            <SimilarResources category={CategoryType.PUBLICATION} currentPostId={resource?.id} />
        </div>
    )
}

export default Publication
